import React, { useEffect, useState } from 'react';

import {
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
  useElements,
  IdealBankElement,
  useStripe,
} from '@stripe/react-stripe-js';

import {
  Grid,
  Button,
  Typography,
  TextField,
  makeStyles,
  Icon,
} from '@material-ui/core';
import CreditCardIcon from '@material-ui/icons/CreditCardTwoTone';

import { verifyPayment, createPayment, verifyPaymentSca } from '@api';
import { useToastNotificationContext } from '@hooks';
import StripeInput from '@components/StripeInput';
import ideal from '@assets/ideal_logo.svg';
import cc from '@assets/cc.png';
import visa from '@assets/visa.svg';
import mastercard from '@assets/mastercard.png';

import { useStyles } from './styles';
import { createPaymentIdeal } from '../../api/payment/createPaymentIdeal';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '../../constants';
import { useHistory } from 'react-router-dom';

const useStylesReddit = makeStyles(theme => ({
  root: {
    color: 'white',
    overflow: 'hidden',
    borderRadius: 4,
    backgroundColor: 'rgb(33,34,49)',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
  },
  focused: {},
}));

function RedditTextField(props) {
  const classes = useStylesReddit();

  return (
    <TextField
      style={{
        border: '1px solid #43566C',
        borderRadius: 9,
        padding: 5,
        paddingLeft: 10,
      }}
      InputProps={{ classes, disableUnderline: true }}
      InputLabelProps={{
        shrink: true,
        style: {
          color: '#43566C',
          padding: 10,
          paddingLeft: 15,
          fontSize: 17,
        },
      }}
      {...props}
    />
  );
}

const ELEMENT_OPTIONS = {
  hidePostalCode: true,
  style: {
    base: {
      fontSize: '18px',
      color: '#424770',
      letterSpacing: '0.025em',
      '::placeholder': {
        color: '#aab7c4',
      },
      marginTop: 20,
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const CustomCardNumberElement = props => (
  <CardNumberElement options={ELEMENT_OPTIONS} {...props} />
);
const CustomCardExpiryElement = props => (
  <CardExpiryElement options={ELEMENT_OPTIONS} {...props} />
);
const CustomCardCvcElement = props => (
  <CardCvcElement options={ELEMENT_OPTIONS} {...props} />
);

const CheckoutForm = props => {
  const classes = useStyles();
  const stripe = useStripe();
  const elements = useElements();
  const { setOrder, setPayment } = props;
  const { setToastNotification } = useToastNotificationContext();
  const [formType, setFormType] = useState('ideal');
  const { t } = useTranslation('checkoutPage');
  const history = useHistory();
  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardNumberType, setCardNumberType] = useState(undefined);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [tip, setTipAmount] = useState(10);
  const [selectedBank, setSelectedBank] = useState('false');
  const [cardCVCComplete, setCardCVCComplete] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [, setOpenIdealModal] = useState(false);

  const [processing, setProcessing] = useState(false);
  const [, setPaymentMethod] = useState(null);
  const [name, setName] = useState('');
  const tipAmounts = ['NO', 5, 10, 15];

  const [, setPaymentResult] = useState({
    status: undefined,
    open: false,
  });

  const transactionFeePercentage = 1;
  const transactionFee = 0.25;
  const addedCreditCardFee =
    (transactionFeePercentage / 100) * props.totalPrice;
  const convertedTip = tip === 'NO' ? 0 : tip;
  const addedTipAmount = (convertedTip / 100) * props.totalPrice;

  const totalPrice =
    formType === 'ideal'
      ? props.totalPrice + transactionFee + addedTipAmount
      : props.totalPrice + addedCreditCardFee + transactionFee + addedTipAmount;

  useEffect(() => {
    if (
      name.length > 0 &&
      cardNumberComplete &&
      cardExpiryComplete &&
      cardCVCComplete
    ) {
      setCardComplete(true);
    } else {
      setCardComplete(false);
    }
  }, [name, cardNumberComplete, cardExpiryComplete, cardCVCComplete]);

  const logEvent = card => event => {
    if (card === 'ideal') {
      if (event.complete) {
        setSelectedBank(event.value);
        setCardComplete(true);
      } else {
        setCardComplete(false);
      }
    }
    if (card === 'number') {
      setCardNumberComplete(event.complete);
      setCardNumberType(event.brand);
    }
    if (card === 'expiry') {
      setCardExpiryComplete(event.complete);
    }
    if (card === 'cvc') {
      setCardCVCComplete(event.complete);
    }
  };
  const handleSubmit = async event => {
    event.preventDefault();
    setProcessing(true);

    if (!stripe || !elements || processing) {
      return;
    }

    if (formType === 'card') {
      if (cardComplete) {
        setProcessing(true);
      }
      const orderId = sessionStorage.getItem('orderId');
      const res_create_payment = await createPayment(orderId, addedTipAmount);

      const payload = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardNumberElement),
        billing_details: {
          name,
        },
      });

      if (payload.error) {
        setToastNotification({
          isShowToast: true,
          toastType: 'error',
          toastMessage: payload.error.message,
          toastKeyTranslate: payload.error.message,
          errorCode: '',
        });

        setPaymentMethod(null);
        setPaymentResult({ status: 'fail', open: true });
      } else {
        setPaymentMethod(payload.paymentMethod);
        try {
          const response = await verifyPayment({
            payment: res_create_payment.payment,
            paymentMethod: payload.paymentMethod,
          });
          if (response.result.requires_action) {
            const ConfirmCardPaymentResponse = await stripe.confirmCardPayment(
              response.result.payment_intent_client_secret,
            );

            await verifyPaymentSca({
              paymentIntentId: ConfirmCardPaymentResponse.paymentIntent.id,
              paymentId: res_create_payment.payment.id,
            });

            if (ConfirmCardPaymentResponse.error) {
              setToastNotification({
                isShowToast: true,
                toastType: 'error',
                toastMessage: ConfirmCardPaymentResponse.error.message,
                toastKeyTranslate: ConfirmCardPaymentResponse.error.message,
              });
              setPaymentResult({ status: 'fail', open: true });
              return false;
            }
          }

          history.push(ROUTES.THANK_YOU_SCREEN, {
            from: ROUTES.ORDERS_SCREEN,
          });

          reset();
          setOrder([]);
          setPayment({});
          setPaymentResult({ status: 'success', open: true });
          setProcessing(false);
        } catch (error) {
          setToastNotification({
            isShowToast: true,
            toastType: 'error',
            toastMessage: 'Mislukt',
            toastKeyTranslate: 'Mislukt',
            errorCode: 'mislukt',
          });
          setProcessing(false);

          setPaymentResult({ status: 'fail', open: true });
        }
      }
    } else {
      // formtype = ideal
      const orderId = sessionStorage.getItem('orderId');
      const { paymentIntent, paymentId } = await createPaymentIdeal(
        orderId,
        addedTipAmount,
      );

      setTimeout(async () => {
        const { error } = await stripe.confirmIdealPayment(
          paymentIntent.client_secret,
          {
            payment_method: {
              ideal: {
                bank: selectedBank,
              },
            },
            return_url: `${window.location.origin}/orders-screen?return=true&paymentId=${paymentId}`,
          },
        );
        if (error) {
          setProcessing(false);
          // Show error to your customer.
          setToastNotification({
            isShowToast: true,
            toastType: 'error',
            toastMessage: error.message,
            toastKeyTranslate: 'Mislukt',
            errorCode: 'mislukt',
          });

          console.error(error.message);
        }
      }, 100);
    }
  };

  const reset = () => {
    setName('');
    setCardNumberComplete(false);
    setCardCVCComplete(false);
    setCardExpiryComplete(false);
    setCardNumberType(undefined);
    setCardComplete(false);
    setProcessing(false);
    window.location.reload(false);
  };

  const handleChange = event => {
    setName(event.target.value);
  };

  const handleIdealConfirm = flag => {
    if (flag) {
      setFormType('ideal');
    } else {
      setFormType('card');
    }
    setOpenIdealModal(false);
  };

  return (
    <form className={classes.Form} onSubmit={handleSubmit}>
      <div style={{ paddingTop: 15, marginBottom: 15 }}>
        <Typography className={classes.PaymentText}>Payment method</Typography>
        <span className={classes.spanLine} />
      </div>
      <div className={classes.card}>
        <div className={classes.divFormType}>
          <Button
            startIcon={
              <Icon>
                <img alt="edit" src={ideal} style={{ display: 'block' }} />
              </Icon>
            }
            style={{ width: '100%', margin: '0 5px 0 10px' }}
            className={
              formType === 'ideal'
                ? classes.formTypeButtonActive
                : classes.formTypeButton
            }
            size="medium"
            onClick={() => {
              if (formType === 'card') {
                handleIdealConfirm(true);
              }
            }}
          >
            {'IDeal'}
          </Button>
          <Button
            style={{ width: '100%', margin: '0 10px 0 5px' }}
            startIcon={
              <img
                alt="edit"
                src={cc}
                style={{ display: 'block', width: 22 }}
              />
            }
            className={
              formType === 'card'
                ? classes.formTypeButtonActive
                : classes.formTypeButton
            }
            size="medium"
            onClick={() => {
              setFormType('card');
            }}
          >
            {'Credit Card'}
          </Button>
        </div>

        <span className={classes.spanLine} />
        {formType === 'card' ? (
          <div
            style={{
              borderBottomLeftRadius: 'inherit',
              borderBottomRightRadius: 'inherit',
            }}
          >
            <div style={{ padding: 20 }}>
              <div style={{ padding: 1, marginBottom: 10 }}>
                <Grid
                  container
                  style={{ border: '1px solid #43566C', borderRadius: 9 }}
                >
                  <Grid item xs={2} style={{ padding: 5 }}>
                    <div className={classes.divCardIcon}>
                      {cardNumberComplete && cardNumberType !== undefined ? (
                        <img
                          key={cardNumberType}
                          src={cardNumberType === 'visa' ? visa : mastercard}
                          alt={cardNumberType}
                          width="50px"
                          align="bottom"
                          style={{ padding: '0 5px' }}
                        />
                      ) : (
                        <CreditCardIcon
                          style={{ fontSize: 34, color: 'lightgray' }}
                        />
                      )}
                    </div>
                  </Grid>
                  <Grid item xs={10}>
                    <RedditTextField
                      label="Card Number"
                      name="ccnumber"
                      style={{ padding: 5, paddingLeft: 10 }}
                      required
                      fullWidth
                      onChange={logEvent('number')}
                      InputProps={{
                        inputComponent: StripeInput,
                        inputProps: {
                          component: CustomCardNumberElement,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </div>
              <Grid container spacing={3}>
                <Grid item xs={6} sm={6}>
                  <RedditTextField
                    label="MM / YY"
                    name="ccexp"
                    required
                    onChange={logEvent('expiry')}
                    fullWidth
                    InputProps={{
                      inputProps: {
                        component: CustomCardExpiryElement,
                      },
                      inputComponent: StripeInput,
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={6}>
                  <RedditTextField
                    label="CVC"
                    name="cvc"
                    required
                    fullWidth
                    onChange={logEvent('cvc')}
                    InputProps={{
                      inputProps: {
                        component: CustomCardCvcElement,
                      },
                      inputComponent: StripeInput,
                      style: {
                        color: 'white',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </div>

            <div className={classes.divCardHolder}>
              <TextField
                className={classes.textCardHolder}
                label="Card holder"
                required
                fullWidth
                value={name}
                onChange={handleChange}
                InputProps={{
                  disableUnderline: true,
                  style: {
                    color: 'white',
                    overflow: 'hidden',
                    borderRadius: 4,
                  },
                }}
                InputLabelProps={{
                  shrink: true,
                  style: {
                    color: '#43566C',
                    padding: 10,
                    paddingLeft: 15,
                    fontSize: 17,
                  },
                }}
              />
            </div>
          </div>
        ) : (
          <div
            style={{
              borderBottomLeftRadius: 'inherit',
              borderBottomRightRadius: 'inherit',
            }}
          >
            <div
              style={{
                padding: 20,
                fontSize: 16,
                color: 'white',
                marginBottom: 20,
              }}
            >
              <div
                htmlFor="ideal-bank-element"
                style={{ paddingTop: 15, marginBottom: 15 }}
              >
                <Typography className={classes.PaymentText}>
                  iDEAL Bank
                </Typography>
              </div>
              <IdealBankElement
                id="ideal-bank-element"
                options={ELEMENT_OPTIONS}
                style={{ padding: 20 }}
                onChange={logEvent('ideal')}
              />
            </div>
          </div>
        )}
      </div>
      <div style={{ paddingTop: 15, marginBottom: 15 }}>
        <Typography className={classes.PaymentText}>
          Support the staff{' '}
          <span role="img" aria-label="star eyes">
            🤩
          </span>
        </Typography>
        <span className={classes.spanLine} />
      </div>

      <div className={classes.divFormType}>
        {tipAmounts.map(amount => (
          <Button
            key={`amount-${amount}`}
            className={
              tip === amount
                ? classes.formTypeButtonActive
                : classes.formTypeButton
            }
            size="medium"
            onClick={() => {
              setTipAmount(amount);
            }}
          >
            {amount}
            {amount !== 'NO' && '%'}
          </Button>
        ))}
      </div>
      <p style={{ color: '#43566C', textAlign: 'center' }}>
        {formType === 'ideal'
          ? t('transactionCostIdeal')
          : t('transactionCostCreditCard')}
      </p>

      <Button
        type="submit"
        className={classes.SubmitButton}
        disabled={processing || !stripe || !cardComplete}
      >
        {processing
          ? 'Processing...'
          : `Pay (${new Intl.NumberFormat('nl-NL', {
              style: 'currency',
              currency: 'EUR',
            }).format(totalPrice)})`}
      </Button>

      <Button
        variant="outlined"
        onClick={props.onCancel}
        className={classes.CancelButton}
      >
        {'Cancel'}
      </Button>
    </form>
  );
};

export default CheckoutForm;
